/**
 * SupervisionMeetingTable
 *
 * Table for supervision meeting notes (Clinical Supervision / Clinical Supervisor QRs).
 * Rows navigate to the supervision meeting form/review page.
 */

import { Button, Text } from '@mantine/core';
import { useMemo } from 'react';
import { UnifyTable, type UnifyTableColumn } from '@/components/UnifyTable';
import type { SupervisionMeetingNoteItem } from '../hooks/useSupervisionMeetingNotes';
import { SupervisionStatusBadge } from './SupervisionStatusBadge';

interface SupervisionMeetingTableProps {
  items: SupervisionMeetingNoteItem[];
  onRowClick: (item: SupervisionMeetingNoteItem) => void;
  /** Label for the primary action button column (defaults to "Open"). */
  actionLabel?: string;
  /** Show the "Author" column (useful in the signing queue where the viewer is the supervisor). */
  showAuthorColumn?: boolean;
}

export function SupervisionMeetingTable({
  items,
  onRowClick,
  actionLabel = 'Open',
  showAuthorColumn = false,
}: SupervisionMeetingTableProps): JSX.Element {
  const formatDate = (d: string | undefined) =>
    d ? new Date(d).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '—';

  const columns = useMemo((): UnifyTableColumn<SupervisionMeetingNoteItem>[] => {
    const base: UnifyTableColumn<SupervisionMeetingNoteItem>[] = [
      {
        key: 'note',
        label: 'Note',
        render: (item) => (
          <Text size="sm" fw={500}>
            {item.title}
          </Text>
        ),
        getValue: (item) => item.title,
      },
      {
        key: 'date',
        label: 'Date',
        render: (item) => <Text size="sm">{formatDate(item.authored)}</Text>,
        getValue: (item) => item.authored ?? '',
      },
      {
        key: 'status',
        label: 'Status',
        render: (item) => <SupervisionStatusBadge status={item.status} size="sm" />,
        getValue: (item) => item.status,
      },
    ];

    if (showAuthorColumn) {
      base.splice(1, 0, {
        key: 'author',
        label: 'Supervisee',
        render: (item) => <Text size="sm">{item.authorName}</Text>,
        getValue: (item) => item.authorName,
      });
    }

    base.push({
      key: 'action',
      label: '',
      render: (item) => (
        <Button
          size="xs"
          variant="light"
          onClick={(e: React.MouseEvent) => {
            e.stopPropagation();
            onRowClick(item);
          }}
        >
          {actionLabel}
        </Button>
      ),
      getValue: () => '',
    });

    return base;
  }, [onRowClick, actionLabel, showAuthorColumn]);

  return (
    <UnifyTable
      withPaper={false}
      columns={columns}
      data={items}
      getRowKey={(item) => item.id}
      onRowClick={onRowClick}
      tableProps={{ style: { width: '100%', tableLayout: 'fixed', minWidth: 600 } }}
    />
  );
}
